import { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'motion/react';
import { Send, Mic, MicOff, Loader2 } from 'lucide-react';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { lightImpact } from '@/utils/haptics';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
}

const ChatInput = ({ onSend, disabled }: ChatInputProps) => {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isListening, transcript, isSupported, startListening, stopListening, clearTranscript } = useVoiceInput();

  useEffect(() => {
    if (transcript) {
      setText((prev) => {
        const newText = prev ? `${prev} ${transcript}` : transcript;
        return newText.trim();
      });
      clearTranscript();
    }
  }, [transcript, clearTranscript]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!text.trim() || disabled) return;
      await lightImpact();
      onSend(text.trim());
      setText('');
    },
    [text, disabled, onSend]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const toggleVoice = useCallback(async () => {
    await lightImpact();
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [text]);

  const canSend = text.trim().length > 0;

  return (
    <form
      onSubmit={handleSubmit}
      className="glass-strong border-t border-slate-200/50 dark:border-slate-800/50 py-3 px-4"
    >
      <div className="flex items-end gap-2 max-w-4xl mx-auto">
        {isSupported && (
          <motion.button
            type="button"
            whileTap={{ scale: 0.88 }}
            onClick={toggleVoice}
            className={`p-3 rounded-full transition-colors flex-shrink-0 min-h-12 min-w-12 flex items-center justify-center
              ${isListening 
                ? 'bg-red-500 text-white shadow-lg shadow-red-500/30' 
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            aria-label={isListening ? 'Stop listening' : 'Start voice input'}
          >
            {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </motion.button>
        )}

        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isListening ? 'Listening...' : 'Type a message...'}
            disabled={disabled}
            rows={1}
            className="w-full px-5 py-3 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100
                       placeholder:text-slate-400 dark:placeholder:text-slate-500
                       focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-none text-[15px] leading-relaxed"
            style={{ minHeight: '48px', maxHeight: '120px' }}
          />
        </div>

        <motion.button
          type="submit"
          whileTap={{ scale: 0.88 }}
          disabled={disabled || !canSend}
          className={`p-3 rounded-full transition-all flex-shrink-0 min-h-12 min-w-12 flex items-center justify-center
            ${canSend && !disabled
              ? 'gradient-bg text-white shadow-glow'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          aria-label="Send message"
        >
          {disabled ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </motion.button>
      </div>
    </form>
  );
};

export default ChatInput;
